import { Component, HostListener, Inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { environment } from "@environment";
import { DOCUMENT } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AbstractComponent, ConfirmationModalComponent, ValidationModalComponent } from "@components";
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  EventFilter,
  EventItem,
  EventStatus,
  FindEventsRequest,
  Lookups,
  Organiser,
  Paging,
  Race,
  Request,
  ValidatorResult
} from "@model";
import { AuthenticationService, EventService, ExportService, LoaderService, RequestService, TOAST_STATE, ToastService } from "@services";
import { ORGANISER_EVENT_ADD, ORGANISER_EVENT_COPY, ORGANISER_EVENT_EDIT, ORGANISER_EVENT_PREVIEW, ORGANISER_EVENTS_MANAGEMENT, ORGANISER_MEASUREMENTS_MANAGEMENT } from "src/app/app-routing.module";
import {BehaviorSubject, debounceTime, distinctUntilChanged, of, Subscription} from 'rxjs';
import {map, switchMap} from "rxjs/operators";

@Component({
  selector: 'app-list-of-events',
  templateUrl: './list-of-events.component.html',
  styleUrls: ['./list-of-events.component.scss'],
})
export class ListOfEventsComponent extends AbstractComponent implements OnInit, Paging<EventItem> {
  public static MEASUREMENTS: string = "measurements";

  @Input() eventStatuses: Lookups[] = [];
  @Input({ required: true }) languages: Lookups[] = [];
  @Input() organisers: Organiser[] = [];
  @Input() events: EventItem[] = [];
  @Input() from: string = 'listOfEvents';

  ListOfEventsComponent = ListOfEventsComponent;
  private latestFetch$ = new BehaviorSubject<{ loadMore: boolean } | null>(null);
  private latestFetchSubscription!: Subscription;

  isAdministrator: boolean = false;
  filter: EventFilter = new EventFilter();
  races: Race[] = [];
  name: string = "";
  dateTo?: Date;
  dateFrom?: Date;
  page: number = 1;
  pageExport: number = 0;
  showFabButton: boolean = false;
  environment: any = environment;
  statusId?: number;
  organiserId?: number;
  hasMoreEvents: boolean = false;
  isLoading: boolean = false;
  queryParamsFirstChange: boolean = true;

  constructor(
    private router: Router,
    private exportService: ExportService,
    private route: ActivatedRoute,
    private requestService: RequestService,
    public authService: AuthenticationService,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private eventService: EventService,
    @Inject(DOCUMENT) private document: Document,
    public translocoService: TranslocoService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
  ) {
    super();
    this.setupLatestRequestHandler();
  }

  ngOnInit() {
    this.eventService.setCurrentEvent(undefined);
    this.handleExploreResult(this.events);
    this.subscribeToRouteDataChange();
    this.isAdministrator = this.authService.isAdminstrator();
  }

  private setupLatestRequestHandler() {
    this.latestFetchSubscription = this.latestFetch$
        .pipe(
            debounceTime(300),
            switchMap((request: { loadMore: boolean } | null) => {
                  if(!request) {
                    return of(null)
                  }
                  if (request.loadMore) {
                    this.loaderService.startLoading();
                  }
                  this.isLoading = true;
                  return this.eventService.fetchAdminEvents(this.createFilter()).pipe(
                      map(value => ({ value, loadMore: request.loadMore }))
                  )
                }
            )
        )
        .subscribe(
            (response: { value: any, loadMore : boolean} | null) => {
              this.isLoading = false
              this.loaderService.dismissLoading()
              if (response) {
                const { value, loadMore } = response;
                this.handleExploreResult(value, loadMore);
              }
            },
            (_) => {
              this.isLoading = false;
              this.loaderService.dismissLoading();
            }
        );
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.activatedRoute.queryParams.pipe(
      debounceTime(environment.fullTextSearchDelay),
      distinctUntilChanged()
    ).subscribe((queryParams: any) => {
      this.name = queryParams.name || "";
      this.organiserId = this.getOrganiserValue(queryParams);
      this.statusId = this.getStatusValue(queryParams);
      if (!this.queryParamsFirstChange && this.isStatusValid(queryParams.status) && this.isOrganiserValid(queryParams.organiser)) {
        this.onExplore(false);
      }
      this.queryParamsFirstChange = false;
    }));
  }

  private changeQueryParams() {
    const queryParams: { [key: string]: any } = {};
    if (this.statusId != null) queryParams['status'] = this.statusId;
    if (this.organiserId) queryParams['organiser'] = this.organiserId;
    if (this.name) queryParams['name'] = this.name;
    this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }

  downloadListOfEvents(type: string) {
    this.exportService.downloadListOfEvents(type, this.createFilterExport());
  }

  @HostListener('window:scroll')
  toggleFabButton(): void {
    this.showFabButton = window.pageYOffset != 0;
  }

  scrollToTop(): void {
    this.document.body.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  }

  trackByFn(index: number, item: EventItem): number | undefined {
    return item.id;
  }

  createFilter() {
    return FindEventsRequest.createEventPageFilter(this.page, {
      name: this.name || undefined,
      status: this.statusId,
      organiserId: this.organiserId,
    });
  }

  createFilterExport() {
    return FindEventsRequest.createEventPageFilterForExport(this.pageExport++, {
      name: this.name || undefined,
      status: this.statusId,
      organiserId: this.organiserId,
    });
  }

  private getStatusValue(queryParams: any) {
    return queryParams.status && this.isStatusValid(queryParams.status) ? +queryParams.status : undefined;
  }

  private isStatusValid(statusId: string | undefined) {
    return !statusId || this.eventStatuses.some(status => status.id === +statusId);
  }

  private getOrganiserValue(queryParams: any) {
    return queryParams.organiser && this.isOrganiserValid(queryParams.organiser) ? +queryParams.organiser : undefined;
  }

  private isOrganiserValid(organiserId: string | undefined) {
    return !organiserId || this.organisers.some(organiser => organiser.id === +organiserId);
  }

  handleExploreResult(value: EventItem[], loadMore?: boolean): void {
    if (value) {
      this.hasMoreEvents = value.length > DEFAULT_PAGE_SIZE;
      value = this.hasMoreEvents ? value.slice(0, DEFAULT_PAGE_SIZE) : value;
      if (loadMore && this.events) value = this.events.concat(value);
    }
    this.setListOfItems(value);
  }

  setListOfItems(value: EventItem[] | null) {
    this.events = value && value.length > 0 ? value : [];
    if (value && value.length > 0) this.page++;
  }

  onExplore(loadMore?: boolean): void {
    if (loadMore) {
      this.loaderService.startLoading();
    } else {
      this.page = 1;
      this.isLoading = true;
      loadMore = false;
    }
    this.latestFetch$.next({loadMore});
  }

  loadMore(): void {
    this.onExplore(true);
  }


  openPublishEventAdminDialog(event: EventItem) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
    modalRef.componentInstance.title = this.translocoService.translate("event.item.publish");
    modalRef.componentInstance.text = this.translocoService.translate("event.item.publish.dialog.text");
    modalRef.result.then(() => this.publishEvent(event), () => { });
  }

  publishEvent(event: EventItem) {
    const eventDetails: EventItem = { ...event, status: EventStatus.ACTIVE };
    this.eventService.publishEvent(eventDetails, () => {
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("event.item.list.publish.success"));
      event.status = EventStatus.ACTIVE;
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.list.publish.error"));
    });
  }

  sendPublishRequest(event: EventItem) {
    this.requestService.sendPublishEventRequest(Request.createPublishEventRequest(event), () => {
      event.status = EventStatus.PUBLISHING;
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("request.sending.success"));
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.error"));
    }).subscribe((val: ValidatorResult | null) => {
      if (val) {
        const modalRef: NgbModalRef = this.modalService.open(ValidationModalComponent, { centered: true, size: 'lg' });
        modalRef.componentInstance.title = this.translocoService.translate("app.validation.error.event");
        modalRef.componentInstance.validatorResult = val;
        modalRef.result.then(() => { }, () => { });
      }
    });
  }

  onPublish(event: EventItem) {
    if (!this.authService.isAdminstrator()) {
      this.sendPublishRequest(event);
    } else {
      this.openPublishEventAdminDialog(event);
    }
  }

  shouldShowPublishButton(event: EventItem) {
    return event.status === EventStatus.INACTIVE;
  }

  onStatusChange(statusId: number | undefined) {
    this.statusId = statusId;
    this.changeQueryParams();
  }

  onOrganiserChange(organiserId: number | undefined) {
    this.organiserId = organiserId;
    this.changeQueryParams();
  }

  handleSearchInput(value: string | Event): void {
    this.name = typeof value === 'string' ? value.trim() : (value.target as HTMLInputElement).value.trim();
    this.changeQueryParams();
  }

  goToOrganiserEventDetails(eventItem: EventItem): void {
    if (eventItem?.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENT_EDIT], { queryParams: { eventId: eventItem.id } });
    }
  }

  goToEventManagement(eventItem: EventItem): void {
    if (eventItem?.id) {
      this.eventService.setCurrentEvent(eventItem);
      const route = this.from === ListOfEventsComponent.MEASUREMENTS ? ORGANISER_MEASUREMENTS_MANAGEMENT : ORGANISER_EVENTS_MANAGEMENT;
      this.router.navigate([route], { queryParams: { eventId: eventItem.id } });
    }
  }

  goToAddEvent(): void {
    this.router.navigate([ORGANISER_EVENT_ADD]);
  }

  goToCopyEvent(eventItem: EventItem): void {
    if (eventItem?.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENT_COPY], { queryParams: { eventId: eventItem.id } });
    }
  }

  openEraseEventConfirmationDialog(event: EventItem): void {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
    modalRef.componentInstance.text = this.translocoService.translate("event.item.list.delete.modal.text");
    modalRef.componentInstance.title = this.translocoService.translate("event.item.list.delete.modal.title");
    modalRef.result.then(() => {
      this.eventService.deleteEvent(event, () => {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("event.item.list.delete.success"));
        this.page = DEFAULT_PAGE_INDEX;
        this.loaderService.startLoading();
        this.eventService.fetchAdminEvents(FindEventsRequest.createEventPageFilter(DEFAULT_PAGE_INDEX)).subscribe((value: EventItem[]) => {
          this.loaderService.dismissLoading();
          this.handleExploreResult(value, false);
        });
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.list.delete.error"));
      });
    }, () => { });
  }

  goToEventPreview(eventItem: EventItem): void {
    if (eventItem?.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENT_PREVIEW], { queryParams: { eventId: eventItem.id } });
    }
  }

  protected readonly EventStatus = EventStatus;

}

